import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Description as DescriptionIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Search as SearchIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutlined as DeleteOutlinedIcon,
} from "@mui/icons-material";

import {
  fetchMyRequisitions,
  fetchRequisitionDetails,
  deleteExistingRequisition,
} from "store/features/company/requisition/requisitionActions";
import {
  selectMyRequisitions,
  selectRequisitionLoading,
  selectRequisitionError,
  selectRequisitionDetails,
  selectRequisitionDetailsLoading,
  selectRequisitionActionLoading,
} from "store/features/company/requisition/requisitionSelectors";
import usePermissions from "hooks/usePermissions";
import EditRequisitionModal from "./EditRequisitionModal";
import rolesSvg from "assets/folders/roles.svg";

import { selectCurrentUser } from "store/features/auth/authSelectors";

const statusFilters = ["all", "SUBMITTED", "APPROVED", "PENDING", "REJECTED"];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  return `INR ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDisplayValue = (...values) => {
  const value = values.find(
    (item) => item !== null && item !== undefined && item !== "",
  );
  return value === undefined ? "-" : value;
};

const getStatusColor = (status, mode) => {
  const lightColors = {
    SUBMITTED: { bg: "#dbeafe", text: "#1e40af" },
    APPROVED: { bg: "#dcfce7", text: "#15803d" },
    REJECTED: { bg: "#fee2e2", text: "#991b1b" },
    PENDING: { bg: "#fef3c7", text: "#92400e" },
  };
  const darkColors = {
    SUBMITTED: { bg: "rgba(59,130,246,0.25)", text: "#93c5fd" },
    APPROVED: { bg: "rgba(34,197,94,0.25)", text: "#86efac" },
    REJECTED: { bg: "rgba(239,68,68,0.25)", text: "#fca5a5" },
    PENDING: { bg: "rgba(245,158,11,0.25)", text: "#fde68a" },
  };

  const colors = mode === "dark" ? darkColors : lightColors;
  return colors[status] || { bg: "#e5e7eb", text: "#374151" };
};

const MyRequisitions = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const currentUser = useSelector(selectCurrentUser);
  const myRequisitions = useSelector(selectMyRequisitions);
  const loading = useSelector(selectRequisitionLoading);
  const error = useSelector(selectRequisitionError);
  const requisitionDetails = useSelector(selectRequisitionDetails);
  const detailsLoading = useSelector(selectRequisitionDetailsLoading);
  const actionLoading = useSelector(selectRequisitionActionLoading);

  const userRole = currentUser?.role || currentUser?.user_type;
  const canView =
    hasPermission("requisition.create") ||
    hasPermission("requisitions.create") ||
    userRole === "Company";
  const canCreate =
    hasPermission("requisition.create") || userRole === "Company";
  const canEdit =
    hasPermission("requisition.create") ||
    hasPermission("requisition.update") ||
    userRole === "Company";
  const canDelete =
    hasPermission("requisition.delete") ||
    hasPermission("requisitions.delete") ||
    userRole === "Company";

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRemarks, setExpandedRemarks] = useState({});

  const [editModal, setEditModal] = useState({
    open: false,
    requisitionId: null,
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    requisition: null,
  });

  const isDark = theme.palette.mode === "dark";
  const safeRequisitions = Array.isArray(myRequisitions) ? myRequisitions : [];

  useEffect(() => {
    dispatch(fetchMyRequisitions());
  }, [dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleDetails = (req) => {
    const requisitionId = req.id;
    const willExpand = !expandedRows[requisitionId];

    setExpandedRows((prev) => ({
      ...prev,
      [requisitionId]: willExpand,
    }));

    if (willExpand && requisitionId && !requisitionDetails[requisitionId]) {
      dispatch(fetchRequisitionDetails(requisitionId));
    }
  };

  const handleOpenEditModal = (req) => {
    setEditModal({
      open: true,
      requisitionId: req.id,
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({
      open: false,
      requisitionId: null,
    });
  };

  const filteredRequisitions = safeRequisitions.filter((req) => {
    const searchableText = [
      req.requisition_no,
      req.requisition_type,
      req.project_name,
      req.department_name,
      req.description,
      req.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      searchTerm === "" || searchableText.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || req.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const isEditable = (req) => {
    const status = String(req.status).toUpperCase();
    return status === "SUBMITTED" || status === "PENDING";
  };

  if (!canView) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: "center",
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
            backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have permission to view requisitions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background: isDark
        //   ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //   : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDark
                  ? "rgba(59,130,246,0.2)"
                  : "rgba(37,99,235,0.1)",
              }}
            >
              <img
                src={rolesSvg}
                alt="My Requisitions"
                style={{ width: 28, height: 28 }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: isDark ? "#ffffff" : "#1e293b",
                }}
              >
                My Requisitions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                View and update purchase requisitions created by you
              </Typography>
            </Box>
          </Stack>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/company/requisition-create")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                backgroundColor:
                  "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                "&:hover": { backgroundColor: "#d97706" },
              }}
            >
              Create Requisition
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filter and Search controls */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "16px",
          backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            placeholder="Search by REQ No, Project, Dept..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", md: "350px" } }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {statusFilters.map((status) => (
              <Chip
                key={status}
                label={status.toUpperCase()}
                clickable
                onClick={() => setStatusFilter(status)}
                color={statusFilter === status ? "primary" : "default"}
                variant={statusFilter === status ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  borderRadius: "8px",
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* Requisitions List Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
          overflow: "hidden",
        }}
      >
        {loading && safeRequisitions.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredRequisitions.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No requisitions found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {searchTerm || statusFilter !== "all"
                ? "Try clearing filters to see results"
                : "Create your first requisition to get started"}
            </Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead
                sx={{ backgroundColor: isDark ? "#1e293b" : "#f8fafc" }}
              >
                <TableRow>
                  <TableCell sx={{ width: "48px" }}></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Requisition No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Purpose / Details
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Requested Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Actions & Remarks
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequisitions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((req) => {
                    const requisitionId = req.id;
                    const isExpanded = Boolean(expandedRows[requisitionId]);
                    const statusColor = getStatusColor(
                      req.status,
                      theme.palette.mode,
                    );
                    const editable = isEditable(req);

                    return (
                      <React.Fragment key={req.id}>
                        <TableRow
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleDetails(req)}
                              sx={{ color: "text.secondary" }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon />
                              ) : (
                                <KeyboardArrowRightIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontWeight: 700, fontSize: "0.875rem" }}
                            >
                              {getDisplayValue(req.requisition_no)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography
                                sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                              >
                                {getDisplayValue(req.requisition_type)}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {req.requisition_type === "PROJECT"
                                  ? `Project: ${getDisplayValue(
                                    req.project_name,
                                  )}`
                                  : `Dept: ${getDisplayValue(
                                    req.department_name,
                                  )}`}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontWeight: 600, color: "#10b981" }}
                            >
                              {formatCurrency(
                                getDisplayValue(req.total_amount, req.amount),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: "0.875rem" }}>
                              {formatDate(req.requested_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getDisplayValue(req.status)}
                              size="small"
                              sx={{
                                backgroundColor: statusColor.bg,
                                color: statusColor.text,
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {editable ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                {canEdit && (
                                  <Tooltip title="Edit requisition (Before approval/rejection)">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<EditOutlinedIcon />}
                                      disabled={Boolean(actionLoading[req.id])}
                                      onClick={() => handleOpenEditModal(req)}
                                      sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderColor: "#d97706",
                                        color: "#d97706",
                                        "&:hover": {
                                          borderColor: "#b45309",
                                          backgroundColor:
                                            "rgba(217,119,6,0.08)",
                                        },
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  </Tooltip>
                                )}
                                {canDelete && (
                                  <Tooltip title="Delete requisition (Before approval/rejection)">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DeleteOutlinedIcon />}
                                      disabled={Boolean(actionLoading[req.id])}
                                      onClick={() =>
                                        setDeleteDialog({
                                          open: true,
                                          requisition: req,
                                        })
                                      }
                                      sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderColor: "#ef4444",
                                        color: "#ef4444",
                                        "&:hover": {
                                          borderColor: "#dc2626",
                                          backgroundColor:
                                            "rgba(239,68,68,0.08)",
                                        },
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : (
                              <Box sx={{ maxWidth: "200px" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.5,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setExpandedRemarks((prev) => ({
                                      ...prev,
                                      [req.id]: !prev[req.id],
                                    }));
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.8rem",
                                      fontStyle: "italic",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: expandedRemarks[req.id]
                                        ? "normal"
                                        : "nowrap",
                                      maxWidth: "150px",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {req.remarks || "No remarks"} ({req.status})
                                  </Typography>
                                  {req.remarks && req.remarks.length > 30 && (
                                    <Typography
                                      sx={{
                                        fontSize: "0.7rem",
                                        color: "primary.main",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {expandedRemarks[req.id] ? "▲" : "▼"}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Collapsible details row */}
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{
                              p: 0,
                              borderBottom: isExpanded ? undefined : 0,
                              backgroundColor: isDark
                                ? "rgba(15,23,42,0.75)"
                                : "#fffbeb",
                            }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ p: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  mb={1}
                                >
                                  Requisition Description:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  mb={2}
                                >
                                  {req.description || "No description provided"}
                                </Typography>

                                {detailsLoading[req.id] ? (
                                  <CircularProgress size={20} />
                                ) : requisitionDetails[req.id]?.items ? (
                                  <Table size="small" sx={{ mt: 1 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Item
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Description
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Qty
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Unit Rate
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Amount
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {requisitionDetails[req.id].items.map(
                                        (item) => (
                                          <TableRow key={item.id}>
                                            <TableCell>
                                              {item.item_name}
                                            </TableCell>
                                            <TableCell>
                                              {item.item_description || "-"}
                                            </TableCell>
                                            <TableCell>
                                              {item.quantity}
                                            </TableCell>
                                            <TableCell>
                                              {formatCurrency(item.unit_rate)}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                color: "#10b981",
                                                fontWeight: 600,
                                              }}
                                            >
                                              {formatCurrency(item.amount)}
                                            </TableCell>
                                          </TableRow>
                                        ),
                                      )}
                                    </TableBody>
                                  </Table>
                                ) : null}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRequisitions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>

      {/* Edit Requisition Modal */}
      {editModal.open && (
        <EditRequisitionModal
          open={editModal.open}
          requisitionId={editModal.requisitionId}
          onClose={handleCloseEditModal}
          onSuccess={() => dispatch(fetchMyRequisitions())}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, requisition: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: isDark ? "#0f172a" : "#ffffff",
            color: isDark ? "#f3f4f6" : "#111827",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete requisition{" "}
            <strong>#{deleteDialog.requisition?.requisition_no}</strong>? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, requisition: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const reqId = deleteDialog.requisition?.id;
              if (reqId) {
                const res = await dispatch(deleteExistingRequisition(reqId));
                if (res?.success) {
                  setDeleteDialog({ open: false, requisition: null });
                  dispatch(fetchMyRequisitions());
                }
              }
            }}
            disabled={Boolean(actionLoading[deleteDialog.requisition?.id])}
            sx={{
              borderRadius: "8px",
              fontWeight: 700,
              backgroundColor: "#ef4444",
            }}
          >
            {actionLoading[deleteDialog.requisition?.id] ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyRequisitions;
